"use client";

import { useState, useEffect, useRef, createContext, useContext } from "react";
import { secureGet } from "@/lib/security/fetch-client";

// 情绪类型
type EmotionType = "joy" | "anger" | "sadness" | "anxiety" | "surprise" | "neutral";

// 情绪图标映射（优化后，更具表现力）
const EMOTION_ICONS: Record<EmotionType, string> = {
  joy: "🎉",
  anger: "😤",
  sadness: "😔",
  anxiety: "😟",
  surprise: "🤯",
  neutral: "😶",
};

// 情绪名称映射
const EMOTION_NAMES: Record<EmotionType, string> = {
  joy: "喜悦",
  anger: "愤怒",
  sadness: "悲伤",
  anxiety: "焦虑",
  surprise: "惊讶",
  neutral: "中性",
};

interface TopicEmotion {
  id: number;
  name: string;
  emotion: EmotionType;
  emotionName: string;
  emotionIcon: string;
  emotionColor: string;
  intensity: number;
}

interface ThermometerData {
  timestamp: string;
  cached?: boolean;
  topicEmotions?: TopicEmotion[];
}

// 情绪数据缓存上下文
interface EmotionCacheContextType {
  emotions: Map<number, TopicEmotion>;
  loading: boolean;
  lastFetch: number | null;
}

const EmotionCacheContext = createContext<EmotionCacheContextType>({
  emotions: new Map(),
  loading: false,
  lastFetch: null,
});

const CACHE_TTL = 5 * 60 * 1000; // 5 分钟缓存

// 情绪数据提供者组件
export function EmotionCacheProvider({ children }: { children: React.ReactNode }) {
  const [emotions, setEmotions] = useState<Map<number, TopicEmotion>>(new Map());
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchEmotions = async () => {
      // 检查缓存是否有效
      if (lastFetchRef.current && Date.now() - lastFetchRef.current < CACHE_TTL && emotions.size > 0) {
        return;
      }

      setLoading(true);
      try {
        const data = await secureGet<ThermometerData>("/api/sentiment/thermometer");
        if (data.topicEmotions) {
          const emotionMap = new Map<number, TopicEmotion>();
          data.topicEmotions.forEach((e) => {
            emotionMap.set(e.id, e);
          });
          setEmotions(emotionMap);
          lastFetchRef.current = Date.now();
        }
      } catch (error) {
        console.error("获取情绪数据失败:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmotions();
    // 每5分钟刷新一次
    const interval = setInterval(fetchEmotions, CACHE_TTL);
    return () => clearInterval(interval);
  }, []); // 空依赖数组，只在组件挂载时执行一次

  return (
    <EmotionCacheContext.Provider value={{ emotions, loading, lastFetch: lastFetchRef.current }}>
      {children}
    </EmotionCacheContext.Provider>
  );
}

// 使用情绪缓存的 Hook
export function useEmotionCache() {
  return useContext(EmotionCacheContext);
}

// 话题情绪标识组件属性
interface TopicEmotionBadgeProps {
  topicId: number;
  showLabel?: boolean;
  size?: "sm" | "md";
}

// 话题情绪标识组件
export function TopicEmotionBadge({ topicId, showLabel = false, size = "sm" }: TopicEmotionBadgeProps) {
  const { emotions } = useEmotionCache();
  const emotion = emotions.get(topicId);

  // 无数据或中性情绪时不显示
  if (!emotion || emotion.emotion === "neutral") {
    return null;
  }

  const iconSize = size === "sm" ? "text-sm" : "text-base";
  const labelSize = size === "sm" ? "text-xs" : "text-sm";

  // 简单显示（仅图标，带 title 属性）
  if (!showLabel) {
    return (
      <span 
        className={`${iconSize} cursor-help`}
        title={`${emotion.emotionName} (强度: ${emotion.intensity})`}
      >
        {emotion.emotionIcon}
      </span>
    );
  }

  // 带标签显示
  return (
    <div 
      className="flex items-center gap-1 cursor-help"
      title={`情绪强度: ${emotion.intensity}/100`}
    >
      <span className={iconSize}>{emotion.emotionIcon}</span>
      <span className={`${labelSize} text-muted-foreground`}>
        {emotion.emotionName}
      </span>
    </div>
  );
}

// 导出情绪图标供其他组件使用
export { EMOTION_ICONS, EMOTION_NAMES };
export type { EmotionType, TopicEmotion };
